// The Bench support tool on a sculpted bench (docs/claude/lattice.md, *Trestles*):
// a click on the bench adds a trestle square to the face under it, a click on a
// trestle removes it, a drag slides one along its own face. The tool's own
// (benchSupportTool.ts) tries this first: a sculpted bench lies over anything
// else a press could reach.
import type {
  ProductInstance,
  SculptedBench,
  SculptTrestle,
  Vec2,
} from '../../shared/types';
import {
  beginGesture,
  dropDrag,
  instLocked,
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  startDrag,
  state,
} from './core';
import {
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import { isPolyRoom } from '../polyRoom';
import {
  sculptBenchAt,
  sculptedBenchesOf,
  sculptLayout,
} from '../sculptBench';
import {
  sculptFace,
  stationOf,
} from '../sculptRun';
import {
  sculptTrestleFootprint,
  sculptTrestleLayout,
  sculptTrestles,
} from '../sculptTrestles';
import { selectRoomKeepingFeature } from '../selection';
import {
  type BenchHover,
  instancesAt,
} from '../main';
import type { ToolAim } from '../toolAim';

/** A trestle slid along its face (`start` as it was, for Esc). It moves once
 *  the drag has gone 4 px from its `press`; a press that never moved removes it. */
export const sculptTrestleTool = {
  drag: null as { instId: string; benchId: string; id: string; press: Vec2; moved: boolean; start: SculptTrestle } | null,
};

/** What a press under the Bench support tool would take on a sculpted bench:
 *  a trestle on it (`hit`), else the trestle a click there would add. */
interface Target { inst: ProductInstance; sb: SculptedBench; hit: SculptTrestle | null; add: SculptTrestle | null }

/** The topmost editable room under `world` with a sculpted bench under the
 *  cursor — or a trestle of one within a few px. */
function targetAt(world: Vec2, mmPerPx: number): Target | null {
  for (const id of instancesAt(world, false)) {
    const inst = state.drawing.instances.find((i) => i.id === id);
    if (!inst || !isPolyRoom(inst)) continue;
    if (instLocked(inst)) return null;
    const p = toInstanceLocal(inst, world);
    if (!pointInPolygon(p, inst.polygon!)) continue;
    const tol = 6 * mmPerPx;
    for (const sb of [...sculptedBenchesOf(inst)].reverse()) {
      for (const t of sculptTrestles(inst, sb)) {
        const fp = sculptTrestleFootprint(t);
        if (pointInPolygon(p, fp) || nearRing(p, fp, tol)) return { inst, sb, hit: t.trestle, add: null };
      }
    }
    const sb = sculptBenchAt(inst, p);
    if (!sb) return null;
    return { inst, sb, hit: null, add: trestleAt(inst, sb, p) };
  }
  return null;
}

function nearRing(p: Vec2, ring: readonly Vec2[], tol: number): boolean {
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
    if (Math.hypot(p.x - a.x - dx * t, p.y - a.y - dy * t) <= tol) return true;
  }
  return false;
}

/** The trestle a click at room-local `p` on `sb` adds: on the nearest face of
 *  its run in front of which `p` stands, at `p`'s station there — none where
 *  it would not stand (the bench too low, by a corner off every face). */
function trestleAt(inst: ProductInstance, sb: SculptedBench, p: Vec2): SculptTrestle | null {
  const lay = sculptLayout(inst, sb);
  if (!lay) return null;
  const faces = lay.run.chain.faces.map((f) => {
    const v = { x: p.x - f.o.x, y: p.y - f.o.y };
    return { f, d: v.x * f.n.x + v.y * f.n.y, pp: v.x * f.t.x + v.y * f.t.y };
  }).filter((c) => c.d >= -1 && c.pp >= c.f.lo - 1 && c.pp <= c.f.hi + 1).sort((a, b) => a.d - b.d);
  for (const c of faces) {
    const t: SculptTrestle = { id: '', host: c.f.host, s: Math.round(stationOf(c.f, Math.max(c.f.lo, Math.min(c.f.hi, c.pp)))) };
    if (sculptTrestleLayout(inst, lay, t)) return t;
  }
  return null;
}

/** The plan polygons of a trestle (room local), resolved on `sb`. */
function footprintOf(inst: ProductInstance, sb: SculptedBench, t: SculptTrestle): Vec2[][] {
  const lay = sculptLayout(inst, sb);
  const tl = lay ? sculptTrestleLayout(inst, lay, t) : null;
  return tl ? [sculptTrestleFootprint(tl)] : [];
}

/** The tool's hover over a sculpted bench: red over a trestle (a click removes
 *  it), green where a click would add one. Undefined when no sculpted bench is
 *  under the cursor (the bench supports' own hover then). */
export function sculptTrestleHoverAt(aim: ToolAim): BenchHover | null | undefined {
  const t = targetAt(aim.world, aim.mmPerPx);
  if (!t) return undefined;
  if (t.hit) return { instId: t.inst.id, polys: footprintOf(t.inst, t.sb, t.hit), kind: 'delete' };
  if (!t.add) return null;
  const polys = footprintOf(t.inst, t.sb, t.add);
  return polys.length ? { instId: t.inst.id, polys, kind: 'add' } : null;
}

/** A press of the Bench support tool on a sculpted bench: on a trestle, it
 *  starts sliding it (a click, never moved, removes it); elsewhere on the bench
 *  it adds a trestle there. False when no sculpted bench is under it. */
export function downSculptTrestle(sp: Vec2, aim: ToolAim): boolean {
  const t = targetAt(aim.world, aim.mmPerPx);
  if (!t) return false;
  if (t.hit) {
    beginGesture();
    selectRoomKeepingFeature(state, t.inst.id);
    sculptTrestleTool.drag = { instId: t.inst.id, benchId: t.sb.id, id: t.hit.id, press: sp, moved: false, start: { ...t.hit, host: { ...t.hit.host } } };
    startDrag('sculpt-trestle');
  } else if (t.add) {
    pushHistory();
    selectRoomKeepingFeature(state, t.inst.id);
    (t.sb.trestles ??= []).push({ ...t.add, id: newId('st') });
  }
  state.benchAddHover = null;
  renderProperties();
  invalidate();
  return true;
}

function dragged(): { inst: ProductInstance; sb: SculptedBench; tr: SculptTrestle } | null {
  const d = sculptTrestleTool.drag;
  const inst = d ? state.drawing.instances.find((i) => i.id === d.instId) : undefined;
  const sb = inst?.sculptedBenches?.find((b) => b.id === d!.benchId);
  const tr = sb?.trestles?.find((x) => x.id === d!.id);
  return inst && sb && tr ? { inst, sb, tr } : null;
}

/** The slide: the trestle follows the cursor along its own face, square to it,
 *  as far as it still stands under the bench. */
export function dragMoveSculptTrestle(_e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const d = sculptTrestleTool.drag, g = dragged();
  if (!d || !g) return false;
  if (!d.moved && Math.hypot(sp.x - d.press.x, sp.y - d.press.y) >= 4) d.moved = true;
  if (!d.moved) return false;
  const f = sculptFace(g.inst, g.tr.host), lay = sculptLayout(g.inst, g.sb);
  if (!f || !lay) return false;
  const p = toInstanceLocal(g.inst, world);
  const pp = Math.max(f.lo, Math.min(f.hi, (p.x - f.o.x) * f.t.x + (p.y - f.o.y) * f.t.y));
  const next = { ...g.tr, s: Math.round(stationOf(f, pp)) };
  if (next.s !== g.tr.s && sculptTrestleLayout(g.inst, lay, next)) { g.tr.s = next.s; invalidate(); }
  return false;
}

/** The slide let go: a press that never moved removes the trestle. */
export function endSculptTrestleDrag(_e: MouseEvent, aim: ToolAim): void {
  const d = sculptTrestleTool.drag, g = dragged();
  if (d && g && !d.moved) {
    g.sb.trestles = g.sb.trestles!.filter((x) => x.id !== d.id);
    if (!g.sb.trestles.length) delete g.sb.trestles;
  }
  sculptTrestleTool.drag = null;
  if (state.tool === 'bench-support') state.benchAddHover = sculptTrestleHoverAt(aim) ?? null;
  renderProperties();
}

/** Esc during the slide: the trestle back where it was. */
export function cancelSculptTrestleDrag(): void {
  const d = sculptTrestleTool.drag, g = dragged();
  if (d && g) { g.tr.host = d.start.host; g.tr.s = d.start.s; }
  sculptTrestleTool.drag = null;
  if (state.dragMode === 'sculpt-trestle') dropDrag();
  invalidate();
}
